import { Controller } from "@hotwired/stimulus"

// Selection state and week paging for the demo gate's half-day chips
// (TASK-174.2, TASK-174.8).
//
// The checkboxes ARE the state — this only keeps the running count honest and
// stops a fourth box being ticked, so the page reads the way the copy promises
// ("pick up to three"). It is deliberately not the enforcement: the controller
// re-validates the submitted slots server-side, because a picker that only
// argues in JavaScript is not a rule.
//
// All four weeks are rendered by the server; this shows one at a time. That is
// the whole design: paging touches nothing but visibility, so a box ticked in
// week 1 is still ticked, still counted and still submitted while week 3 is on
// screen. Nothing here re-requests anything, and nothing here can lose a tick.
export default class extends Controller {
  static targets = ["slot", "count", "week", "weekLabel", "back", "forward"]
  static values = {
    max: { type: Number, default: 3 },
    // Which week opens. Nought for a first ask; "Change my times" opens on the
    // week holding the first time already chosen.
    week: { type: Number, default: 0 },
  }

  connect() {
    this.showWeek(this.weekValue)
    this.refresh()
  }

  previousWeek() {
    this.showWeek(this.weekValue - 1)
  }

  nextWeek() {
    this.showWeek(this.weekValue + 1)
  }

  showWeek(index) {
    if (this.weekTargets.length === 0) return

    const last = this.weekTargets.length - 1
    const shown = Math.min(Math.max(index, 0), last)
    this.weekValue = shown

    // The hidden attribute rather than a class: Tailwind's reset makes it
    // !important, so it beats the grid display on the element it sits on.
    this.weekTargets.forEach((week, position) => {
      week.hidden = position !== shown
    })

    if (this.hasWeekLabelTarget) {
      this.weekLabelTarget.textContent = this.weekTargets[shown].dataset.weekLabel || ""
    }

    // Disabled rather than wrapping: there is nothing before the next working
    // day and nothing after the horizon, and a chevron that silently does
    // nothing is worse than one that says so.
    if (this.hasBackTarget) this.backTarget.disabled = shown === 0
    if (this.hasForwardTarget) this.forwardTarget.disabled = shown === last
  }

  refresh() {
    const chosen = this.slotTargets.filter((slot) => slot.checked)

    // Only the unchosen boxes close once the limit is reached, so a member can
    // always change their mind by unticking one first — including one on a week
    // they are not currently looking at.
    this.slotTargets.forEach((slot) => {
      slot.disabled = !slot.checked && chosen.length >= this.maxValue
    })

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${chosen.length} of ${this.maxValue} selected`
    }
  }
}
